import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { clearStoredToken, getStoredToken, setStoredToken } from '../utils/tokenStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(getStoredToken());

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      clearStoredToken();
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (username, password) => {
    const res = await api.post('/auth/login', { username, password });
    const { token: newToken, user: userData } = res.data;
    setStoredToken(newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearStoredToken();
    setToken(null);
    setUser(null);
  };

  const changePassword = async (oldPassword, newPassword) => {
    await api.post('/auth/change-password', 
      { old_password: oldPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser(prev => ({ ...prev, must_change_password: false }));
  };

  const isAdministrator = user?.role === 'administrator';
  const isAdminFinance = user?.role === 'admin_finance';
  const isStaffFinance = user?.role === 'staff_finance';
  const isAdmin = isAdministrator;
  const isFinance = isAdminFinance || isStaffFinance;
  const hasGlobalAccess = isAdministrator || !!user?.global_access;
  const canManageTransactions = isAdministrator || isAdminFinance;
  const canManageSystem = isAdministrator;

  const canAccessCabang = (cabangId) => {
    if (hasGlobalAccess) return true;
    if (!cabangId) return false;
    return user?.assigned_cabang?.includes(cabangId);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    changePassword,
    isAdmin,
    isFinance,
    isAdministrator,
    isAdminFinance,
    isStaffFinance,
    hasGlobalAccess,
    canManageTransactions,
    canManageSystem,
    canAccessCabang,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
